/*
   EXAMPLE DATA FIELD
   npiNumber: {
       isDirty: false,  <---- determines if a user has interacted with a field (set to true on blur)
       *label: '',
       fieldInputBreakpoints: {
                xs: 12,
                sm: 12,
                md: 12,
                lg: 12
           }
       },
       fieldLabelBreakPoints: {
            xs: 12,
            sm: 12,
            md: 12,
            lg: 12
       },
       value: '',     <--- inserted into the object by context when loaded, default is empty string to avoid input control warnings in console
       error: [],     <--- an array of error messages populated by validations
       onBlur: (value) => { logic }, <!--- give a place to perform logic based on values after a change has happened
       onChange: (value) => { logic },  <!--- give a place to perform logic based on values after a change has happened
       style: {}, <!--- css properties
       type: 'string',  <!---- what type of input is it [info, text, phone, radio, prefilled] default is text
       validations: [],   <--- an array of validations for each field can be a function name or an actual function
       visibility: (value) => { logic },    <--- a function that determines if a field should be visible or not
   }
*/

import {fetchDrugForm} from './api-service';
import {fetchPrescriberNpi} from './api-service';
import {apiStatus} from './enums';
import {sortDrugList} from './apiHelpers';
import {colors} from './colors';

const extractPhoneDigits = (phone) => {
    if (phone) {
        const matches = phone.match(/[0-9]+/g)
        return (matches ? matches.join('') : null)
    }
    return null
}

const extractNpi = (npi) => {
  if (npi) {
    const matches = npi.match(/[0-9]+/g)
    return (matches ? matches.join('') : null)
  }
  return null
}
const transformProviderType = (type) => {
    if(type && type === 'SVP'){
        return 'PH'
    }
    else if(type){
        return type
    }
    return null
}

const normalizeQuantity = quantity => {
    if(quantity){
        const integers = quantity.split('.')[0]
        const decimals = quantity.split('.')[1]
        const normalizedDecimals = decimals ? decimals.length > 3 ? decimals.slice(0, 3) : decimals : ''
        const normalizedIntegers = integers ? Number(integers).toString() : ''
        const normalizedQuantity = decimals ? normalizedIntegers + '.' + normalizedDecimals : normalizedIntegers
        return normalizedQuantity
    }
}

const quantityValidation = value => {

    const isQuantityValid = value => {

        const validate = (nums) => {
            return nums ? Number.isInteger(Number(nums)) : true
        }

        if (value) {
            const integers = value.split('.')[0]
            const decimals = value.split('.')[1]
            const areIntegersValid = validate(integers)
            const areDecimalsValid = validate(decimals)
            return  areIntegersValid &&
                    areDecimalsValid &&
                    parseFloat(value) > 0 &&
                    parseFloat(value) < 10000000
        }
        return true
    }
    return {
        valid: isQuantityValid(value),
        msg: 'Quantity must be a number greater than 0 and less than 10,000,000'
    }
}

const PaFormFields = {
  // step 1 non-hidden fields
  npiNumber: {
    label: "NPI Number",
    type: "prefilled",
    fieldLabelStyle: {
      paddingTop: "0",
    },
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 6,
      md: 6,
      lg: 3,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 6,
      md: 6,
      lg: 9,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 6,
      md: 5,
      lg: 4,
    },
  },
  providerName: {
    label: "Provider Name",
    type: "prefilled",
    fieldLabelStyle: {
      paddingTop: "0",
    },
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 6,
      md: 6,
      lg: 3,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 6,
      md: 6,
      lg: 9,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 6,
      md: 5,
      lg: 4,
    },
  },
  providerType: {
    label: "Provider Type",
    type: "prefilled",
    render: (fields, appContext) => appContext.provider.providerDesc,
    sanitize: transformProviderType,
    fieldLabelStyle: {
      paddingTop: "0",
    },
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 6,
      md: 6,
      lg: 3,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 6,
      md: 6,
      lg: 9,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 6,
      md: 5,
      lg: 4,
    },
  },
  enteralNdc: {
    label: "Please enter the 11-digit NDC number for Enteral Nutrition Product",
    type: "text",
    inputProps: { maxlength: 11 },
    sanitize: extractNpi,
    fieldLabelTextStyle: {
      color: colors.magellanRed,
    },
    fieldLabelBreakPoints: {
      xs: 4,
      sm: 4,
      md: 4,
      lg: 4,
    },
    fieldInputBreakpoints: {
      xs: 8,
      sm: 8,
      md: 8,
      lg: 8,
    },
  },
  providerFax: {
    label: "Fax #",
    type: "phone",
    sanitize: extractPhoneDigits,
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 2,
      md: 3,
      lg: 3,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 3,
      md: 3,
      lg: 9,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 5,
      lg: 4,
    },
    validations: ["required", "phone"],
  },
  providerPhone: {
    label: "Phone #",
    type: "phone",
    sanitize: extractPhoneDigits,
    fieldLabelStyle: {
      paddingTop: "1rem",
    },
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 2,
      md: 3,
      lg: 3,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 3,
      md: 3,
      lg: 9,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 5,
      lg: 4,
    },
  },

  providerIsPrescriber: {
    label: "Is the requesting provider same as the prescriber",
    defaultValue: 'no',
    visibility: (fields) => {
      return false
    },
    suffix: "?",
    fieldLabelStyle: {
      paddingTop: "0.7rem",
      whiteSpace: "break-spaces",
    },
    fieldLabelBreakPoints: {
      lg: 3,
      md: 4,
      sm: 7,
    },
    fieldInputBreakpoints: {
      lg: 9,
      md: 8,
      sm: 5,
    },
    fieldBreakpoints: {
      xs: 12,
    },
    onBlur: (value, { setFields }) => {
      if (value === "yes") {
        setFields({
          prescriberNPI: { value: "", isDirty: false },
          prescriberFirstName: { value: "", isDirty: false },
          prescriberLastName: { value: "", isDirty: false },
          prescriberFax: { value: "", isDirty: false },
          prescriberPhone: { value: "", isDirty: false },
        });
      }
    },
    type: "boolean",
    validations: ["required"],
  },

  // step 1 hidden fields
  prescriberNPI: {
    label: "Prescriber NPI",
    validations: ["required"],
    inputProps: { maxlength: 10 },
    visibility: () => true,
    type: "npiInput",
    onChange: async (value,
      { fields, setFields, setState, create: { enableNext, disableNext } }
      ) => {
        let error = null;
        if (!value) {
          error = "Prescriber NPI is required."
        } else if (!/^[0-9]{10}$/.test(value)) {
          error = "Prescriber NPI must be 10 digits."
          disableNext()
        }
        setFields({ 
          prescriberNpiSuccess: { value: "", isDirty: false, options: [] },
          prescriberNPI: { ...fields.prescriberNPI, value, error }
        });
        if (value.length === 10 && !error) {
          try {
            setState((prev) => ({
              ...prev,
              create: {
                ...prev.create,
                status: apiStatus.LOADING,
              },
            }));
            const prescriberNpi = value;
            const prescriberNpiSearch = {prescriberNpi: prescriberNpi, providerType: fields.providerType.value};
            const res = await fetchPrescriberNpi(prescriberNpiSearch);
            const response = res[0];

            const newError = response ? null : "Invalid NPI, Please enter a valid NPI."

            setFields({
              prescriberNPI: {
                ...fields.prescriberNPI,
                value,
                error: newError,
                isTouched: true
              },
              prescriberNpiSuccess: { value: response?.npi || "", isDirty: false, options: [] }
            })

            response ? enableNext() : disableNext();

            setState((prev) => ({
              ...prev,
              create: {
                ...prev.create,
                status: apiStatus.SUCCESS,
              },
            }));

          } catch (error) {
            setFields({
              prescriberNPI: { ...fields.prescriberNPI, value, error: "Validation failed. Try again.", isTouched: true}
            })
            disableNext();
          }
        }
      },
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 3,
      md: 3,
      lg: 3,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 4,
      md: 4,
      lg: 9,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 4,
    },
  },
  prescriberNpiSuccess: {},
  prescriberFirstName: {
    label: "First Name",
    validations: ["required"],
    inputProps: { maxlength: 15 },
    visibility: (fields) => {
      return true
    },
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 3,
      md: 3,
      lg: 3,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 4,
      md: 4,
      lg: 9,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 4,
    },
  },
  prescriberLastName: {
    label: "Last Name",
    validations: ["required"],
    inputProps: { maxlength: 15 },
    visibility: () => true,
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 3,
      md: 3,
      lg: 3,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 4,
      md: 4,
      lg: 9,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 4,
    },
  },
  prescriberFax: {
    label: "Fax #",
    type: "phone",
    sanitize: extractPhoneDigits,
    visibility: () => true,
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 3,
      md: 3,
      lg: 3,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 4,
      md: 4,
      lg: 9,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 4,
    },
    validations: ["required", "phone"]
  },
  prescriberPhone: {
    label: "Phone #",
    type: "phone",
    sanitize: extractPhoneDigits,
    validations: ["required", "phone"],
    visibility: () => true,
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 3,
      md: 3,
      lg: 3,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 4,
      md: 4,
      lg: 9,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 4,
    },
  },

  // step 2
  submitterHeader: {
    label:
      "Please enter your name and best contact phone number. Also please provide a fax number that can receive HIPAA secured communication",
    type: "header",
    fieldLabelStyle: {
      whiteSpace: "unset",
    },
    fieldLabelBreakPoints: {
      xs: 11,
      sm: 11,
      md: 11,
      lg: 11,
    },
    fieldInputBreakpoints: {
      xs: 1,
      sm: 1,
      md: 1,
      lg: 1,
    },
  },
  submitterFirstName: {
    label: "Submitter First Name",
    validations: ["required"],
    inputProps: { maxlength: 15 },
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 4,
      md: 3,
      lg: 6,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 6,
      md: 6,
      lg: 6,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 5,
    },
  },
  submitterLastName: {
    label: "Submitter Last Name",
    validations: ["required"],
    inputProps: { maxlength: 20 },
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 4,
      md: 3,
      lg: 6,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 6,
      md: 6,
      lg: 6,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 5,
    },
  },
  submitterContactPhone: {
    label: "Contact Phone Number",
    type: "phone",
    sanitize: extractPhoneDigits,
    validations: ["required", "phone"],
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 4,
      md: 3,
      lg: 6,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 6,
      md: 6,
      lg: 6,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 5,
    },
  },
  submitterHippaSecureFax: {
    label: "HIPAA Secure Fax Number",
    type: "phone",
    sanitize: extractPhoneDigits,
    validations: ["required", "phone"],
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 4,
      md: 3,
      lg: 6,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 6,
      md: 6,
      lg: 6,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 5,
    },
  },

  // step 3
  beneficiaryHeader: {
    label: "Member Information",
    upperDescriptionHTML: {
      __html:
        '<p>If your PA request is for a newborn infant or transplant donor who is not actively enrolled in Medi-Cal Rx, please submit your PA request via fax. Download the <a href="/cms/medicalrx/static-assets/documents/provider/forms-and-information/Medi-Cal_Rx_PA_Request_Form.pdf" target="_blank" style="font-weight: bold">Medi-Cal Rx PA fax form</a>.</p>',
    },
    type: "header",
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 12,
    },
  },
  beneficiaryFirstName: {
    label: "Member First Name",
    validations: ["required"],
    inputProps: { maxlength: 15 },
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 4,
      md: 6,
      lg: 5,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 6,
      md: 6,
      lg: 7,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 5,
      lg: 4,
    },
  },
  beneficiaryLastName: {
    label: "Member Last Name",
    validations: ["required"],
    inputProps: { maxlength: 25 },
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 4,
      md: 6,
      lg: 5,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 6,
      md: 6,
      lg: 7,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 5,
      lg: 4,
    },
  },
  beneficiaryID: {
    label: "Member ID",
    validations: ["required", "beneficiaryID"],
    inputProps: {
      maxlength: 9,
      // style: { // flagged by siteImprove for as a Q level compliance issue
      //   textTransform: "uppercase",
      // },
    },
    onBlur: (value, { fields, setFields }) => {
      if (value) {
        setFields({ beneficiaryID: value.toUpperCase() });
      }
    },
    tooltip: `Please enter the beneficiary's Cardholder ID Number (CIN). This will be the first 9 characters of the Beneficiary Identification Card (BIC) number.
        If you have any issues and would like to speak to a representative please call the customer service center at 1-800-977-2273.`,
    tooltipStyle: {
      fontSize: "0.85rem",
    },
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 4,
      md: 6,
      lg: 5,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 6,
      md: 6,
      lg: 7,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 5,
      lg: 4,
    },
  },
  beneficiaryDOB: {
    label: "Member DOB",
    type: "date",
    validations: ["required", "dateNotInFuture", "dateFmt"],
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 4,
      md: 6,
      lg: 5,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 6,
      md: 6,
      lg: 7,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 5,
      lg: 4,
    },
    maxDate: new Date(),
  },

  drugInfoHeader: {
    label: "Drug Information",
    type: "header",
    fieldLabelStyle: {
      paddingTop: "0.7rem",
      whiteSpace: "break-spaces",
    },
    fieldLabelBreakPoints: {
      xs: 11,
      sm: 11,
      md: 11,
      lg: 11,
    },
    fieldInputBreakpoints: {
      xs: 1,
      sm: 1,
      md: 1,
      lg: 1,
    },
  },
  enteralWarning: {
    label: "Request for Enteral Nutrition Products must be submitted using NDC.",
    visibility: (fields) => fields.isCompound.value !== "yes",
    type: "info",
    fieldLabelTextStyle: {
      color: colors.magellanRed,
      fontStyle: 'italic',
    },
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 12,
      xl: 12
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 7,
      lg: 6,
      xl: 6
    },
    fieldLabelStyle: {
      paddingTop: "0.5rem",
    },
    suffix: ' '
  },
  dawTrialCode: {
    label: "Should the request be reviewed for Substitution Not Allowed (DAW-1)",
    suffix: "?",
    type: "boolean",
    beforeonBlur: (value) => {
      if (value === "yes") {
        return true;
      } else if (value === "no") {
        return true;
      } else {
        return true;
      }
    },
    onBlur: (value, { setFields}) => {
      if (value === "yes") {
        const change = {
          dawSelectionCode: { value: "1", isDirty:false, options: [
            { value: "0", label: "0 - No Product Selection Ind" },
            { value: "1", label: "1 - No Substitution-Prescriber" },
            { value: "2", label: "2 - Dispensed Patient Request" },
            { value: "3", label: "3 - Dispensed Pharmacist Selected" },
            { value: "4", label: "4 - Generic Drug Not in Stock" },
            { value: "5", label: "5 - Dispensed Brand as Generic" },
            { value: "6", label: "6 - Override" },
            { value: "7", label: "7 - No Substitue-Brand by Law" },
            { value: "8", label: "8 - Generic Drug Unavailable" },
            { value: "9", label: "9 - Other" },
          ]}
        };
        setFields(change);
      } else {
        const change = {
          dawSelectionCode: { value: "0", isDirty:false, options: [
              { value: "0", label: "0 - No Product Selection Ind" },
              { value: "2", label: "2 - Dispensed Patient Request" },
              { value: "3", label: "3 - Dispensed Pharmacist Selected" },
              { value: "4", label: "4 - Generic Drug Not in Stock" },
              { value: "5", label: "5 - Dispensed Brand as Generic" },
              { value: "6", label: "6 - Override" },
              { value: "7", label: "7 - No Substitue-Brand by Law" },
              { value: "8", label: "8 - Generic Drug Unavailable" },
              { value: "9", label: "9 - Other" },
            ]}
        };
        setFields(change);
      }
    },
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 8,
      md: 8,
      lg: 8,
      xl: 7,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 4,
      md: 4,
      lg: 4,
      xl: 4,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 11,
      lg: 8,
      xl: 6,
    },
    fieldLabelStyle: {
      paddingTop: "0.4rem",
      paddingRight: "0.1rem",
      whiteSpace: "break-spaces",
    },
  },
  compoundsDisplay: {
    visibility: (fields) => fields.isCompound.value === "yes",
  },
  isCompound: {
    label: "Is the requested medication a compound drug",
    suffix: "?",
    beforeonBlur: (value, context) => {
      const { drugName, compounds } = context.fields;
      if (value === "yes" && !drugName.value) {
        return true;
      } else if (value === "no" && !compounds.hasValue) {
        return true;
      } else {
        return true;
      }
    },
    onBlur: (value, { setFields, create: { enableNext } }) => {
      enableNext();
      if (value === "yes") {
        const change = {
          trialAPIStatus: { value: "", isDirty: false },
          searchBy: { value: "", isDirty: false, options: [] },
          ndcInput: { value: "", isDirty: false, options: [] },
          drugName: { value: "", isDirty: false, options: [] },
          drugStrength: { value: "", isDirty: false, options: [] },
          drugForm: { value: "", isDirty: false, options: [] },
          quantity: { value: "", isDirty: false, options: [] },
          daysSupply: { value: "", isDirty: false, options: [] },
          drugFormData: { value: "", isDirty: false, options: [] },
          drugNotFound: { value: "", isDirty: false, options: [] },
        };
        setFields(change);
      } else {
        const change = {
          compoundIngredientCount: { value: 2 },
          compoundDosageFormCd: { value: "", isDirty: false, options: [] },
          compoundDispUnitFormInd: { value: "", isDirty: false, options: [] },
        };
        setFields(change);
      }
    },
    type: "boolean",
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 8,
      md: 8,
      lg: 8,
      xl: 7,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 4,
      md: 4,
      lg: 4,
      xl: 4,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 11,
      lg: 8,
      xl: 6,
    },
    fieldLabelStyle: {
      paddingTop: "0.4rem",
      paddingRight: "0.1rem",
      whiteSpace: "break-spaces",
    },
  },
  searchBy: {
    label: "Search By",
    validations: ["required"],
    visibility: (fields) => fields.isCompound.value !== "yes",
    type: "drugNameNDCRadio",
    onBlur: (value, { setFields }) => {
        const change = {
          trialAPIStatus: { value: "", isDirty: false },
          ndcInput: { value: "", isDirty: false, options: [] },
          ndcResult: {value: "", isDirty: false, options: []},
          drugName: { value: "", isDirty: false, options: [] },
          quantity: { value: "", isDirty: false, options: [] },
          daysSupply: { value: "", isDirty: false, options: [] },
          drugFormData: { value: "", isDirty: false, options: [] },
          drugNotFound: { value: "", isDirty: false, options: [] },
        };
        setFields(change);
    },
    suffix: "",
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 3,
      md: 4,
      lg: 5,
      xl: 4
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 6,
      md: 8,
      lg: 7,
      xl: 8
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 5,
      lg: 4,
      xl: 3
    },
    fieldLabelStyle: {
      paddingTop: "0.5rem",
    },
  },
  ndcInput: {
    validations: ["required", "ndc", "validNDC_enteralNdc"],
    inputProps: { maxLength: 11 },
    visibility: (fields) => fields.searchBy.value === "ndc",
    label: "NDC",
    type: "ndcInput",
    onChange: async (value,
      { setFields, setState, create: { enableNext, disableNext } }
      ) => {
        setFields({ 
          trialAPIStatus: { value: "", isDirty: false, options: [] },
          enteralNdc: { value: "", isDirty: false, options: [] }
        });
        if (value.length === 11) {
          try {
            setState((prev) => ({
              ...prev,
              create: {
                ...prev.create,
                status: apiStatus.LOADING,
              },
            }));
            const ndc = value;
            const ndcSearch = {searchBy: "ndc", ndc: ndc};
            const res = await fetchDrugForm(ndcSearch);
            const response = res[0];
            setState((prev) => ({
              ...prev,
              create: {
                ...prev.create,
                status: apiStatus.SUCCESS,
              },
            }));
            if (response) {
              enableNext();
              const change = {
                ndcResult: { value: res[0].drugLabelName, isDirty: false, options: [] },
                drugName: { value: res[0].drugLabelName, isDirty: false, options: [] },
                drugForm: { value: res[0].drugForm, isDirty: false, options: [] },
                drugStrength: { value: res[0].drugStrength, isDirty: false, options: [] },
                ndc: { value: ndc, isDirty: false, options: [] },
                quantity: { value: "", isDirty: false, options: [] },
                daysSupply: { value: "", isDirty: false, options: [] },
                enteralNdc: { value: "", isDirty: false, options: [] }
              }
              setFields(change);
            } else {
              disableNext();
              const change = {
                ndcResult: { value: "", isDirty: false, options: [] },
                drugName: { value: "", isDirty: false, options: [] },
                drugForm: { value: "", isDirty: false, options: [] },
                drugStrength: { value: "", isDirty: false, options: [] },
                ndc: { value: "", isDirty: false, options: [] },
                quantity: { value: "", isDirty: false, options: [] },
                daysSupply: { value: "", isDirty: false, options: [] },
                enteralNdc: { value: ndc, isDirty: false, options: [] }
              }
              setFields(change);
            }
          } catch (error) {
            disableNext();
          }
        }
      },
      fieldLabelBreakPoints: {
        xs: 12,
        sm: 3,
        md: 2,
        lg: 3,
      },
      fieldInputBreakpoints: {
        xs: 12,
        sm: 6,
        md: 6,
        lg: 9,
      },
      fieldBreakpoints: {
        xs: 12,
        sm: 12,
        md: 12,
        lg: 4,
      },
    fieldLabelStyle: {
      paddingTop: "0.5rem"
    },
  },
  ndcResult: {
    visibility: (fields) => fields.ndcResult.value && fields.searchBy.value === "ndc",
    label: "Drug Name",
    type: "ndcResult",
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 3,
      md: 2,
      lg: 3,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 6,
      md: 6,
      lg: 9,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 4,
    },
    fieldLabelStyle: {
      paddingTop: "0.5rem",
    }
  },
  drugFormData: {},
  drugName: {
    validations: ["required", "drugNotFailed"],
    visibility: (fields) => fields.searchBy.value === "drugName",
    label: "Drug Name",
    type: "drugList",
    onChange: async (
      value,
      { setFields, setState, create: { data, enableNext, disableNext } }
    ) => {
      setFields({ trialAPIStatus: "" });
      enableNext();

      let _change;

      if (value) {
        try {
          setState((prev) => ({
            ...prev,
            create: {
              ...prev.create,
              status: apiStatus.LOADING,
            },
          }));
          const drug = data.filter((d) => d.drugLabelName === value)[0];
          const url = {drugLabelName: encodeURIComponent(drug.drugLabelName), searchBy: "drugName"};
          const res = await fetchDrugForm(url);
          const response = res[0];
          setState((prev) => ({
            ...prev,
            create: {
              ...prev.create,
              status: apiStatus.SUCCESS,
            },
          }));
          if (response) {
            sortDrugList(res, "form");
            const drugForms = [];
            res.forEach((drug) => {
              if (!drugForms.includes(drug.drugForm)) {
                drugForms.push(drug.drugForm);
              }
            });

            const responseNDC = res[0].ndc[0];
            if (responseNDC) {
              setFields({
                ndc: { value: responseNDC, isDirty: false, options: [] },
                drugForm: { value: res[0].drugForm, isDirty: false, options: [] },
                drugStrength: { value: res[0].drugStrength, isDirty: false, options: [] }
              });
            } else {
              setFields({
                ndc: { value: "", isDirty: false, options: [] },
                drugForm: { value: "", isDirty: false, options: [] },
                drugStrength: { value: "", isDirty: false, options: [] }
              });
            };
          } else {
            setState((prev) => {
              const newState = {
                ...prev,
                bannerProps: {
                  message: "Could not find this drug name. Search by NDC.",
                  severity: "error",
                  open: true,
                },
              };
              return newState;
            });
            disableNext();
          }
        } catch (error) {
          setState((prev) => {
            const newState = {
              ...prev,
              create: {
                ...prev.create,
                status: apiStatus.ERROR,
              },
              bannerProps: {
                message: error.message,
                severity: "error",
                open: true,
              },
            };
            return newState;
          });
        }
      }

      const change = {
        quantity: { value: "", isDirty: false, options: [] },
        daysSupply: { value: "", isDirty: false, options: [] },
        drugFormData: { value: "", isDirty: false, options: [] },
        ..._change,
      };

      setFields(change);
    },
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 3,
      md: 2,
      lg: 3,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 6,
      md: 6,
      lg: 9,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 5,
    },
    fieldLabelStyle: {
      paddingTop: "0.5rem",
    },
  },
  drugForm: {
    label: "Drug Form",
    type: "select",
    selectSize: 200,
    visibility: (fields) =>
      fields.drugName.value &&
      fields.drugFormData.value &&
      fields.drugFormData.value.length > 0,
    onChange: async (value, { fields, setFields, create: { enableNext } }) => {
      setFields({ trialAPIStatus: "" });
      enableNext();
      const drugFormData = fields.drugFormData.value.filter((item) => {
        return item.drugForm === value;
      });
      sortDrugList(drugFormData, "strength");
      const drugStrengths = [];
      drugFormData.forEach((drug) => {
        if (!drugStrengths.includes(drug.drugStrength)) {
          drugStrengths.push(drug.drugStrength);
        }
      });
      const change = {
        drugStrength: { value: "", isDirty: false, options: drugStrengths },
      };
      setFields(change);
    },
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 3,
      md: 2,
      lg: 3,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 6,
      md: 6,
      lg: 9,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 4,
    },
    fieldLabelStyle: {
      paddingTop: "0.5rem",
    },
  },

  drugStrength: {
    label: "Drug Strength",
    type: "select",
    selectSize: 200,
    visibility: (fields) => fields.drugForm.value,
    onChange: async (value, { fields, setFields, create: { enableNext } }) => {
      setFields({ trialAPIStatus: "" });
      enableNext();
      const drugFormData = fields.drugFormData.value.filter((item) => {
        return (
          item.drugForm === fields.drugForm.value && item.drugStrength === value
        );
      });
      const ndcs = drugFormData.map((drug) => drug.ndc).flat();
      setFields({ ndc: { value: ndcs[0], isDirty: false, options: ndcs } });
    },
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 3,
      md: 2,
      lg: 3,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 6,
      md: 6,
      lg: 9,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 4,
    },
    fieldLabelStyle: {
      paddingTop: "0.5rem",
    },
  },
  trialAPIStatus: {},
  proactivePaMessage: {},
  rejectCode1: {},
  internalErrorCodes1: {},
  externalConditionCode: {},
  internalConditionCode: {},
  tReqId: {},
  failedDrugList: {},
  quantity: {
    label: "Quantity",
    type: "number",
    inputProps: { maxlength: 11 },
    visibility: (fields) => (fields.drugName.value && fields.drugForm.value && fields.drugStrength.value && fields.searchBy.value === "drugName") || (fields.ndcResult.value && fields.searchBy.value === "ndc"),
    validations: ["required", quantityValidation], 
    onBlur: (value, { fields, setFields }) => {
      if (fields.quantity.isValid) {
        setFields({ quantity: normalizeQuantity(value) });
      }
    },
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 3,
      md: 2,
      lg: 3,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 6,
      md: 6,
      lg: 8,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 5,
    },
  },

  daysSupply: {
    label: "Days Supply",
    type: "number",
    inputProps: { maxlength: 3 },
    visibility: (fields) => (fields.drugName.value && fields.drugForm.value && fields.drugStrength.value && fields.searchBy.value === "drugName") || (fields.ndcResult.value && fields.searchBy.value === "ndc"),
    validations: ["required", "positiveInteger"],
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 3,
      md: 2,
      lg: 3,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 6,
      md: 6,
      lg: 8,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 5,
    },
  },

  ndc: {
    label: "NDC",
    type: "select",
    visibility: (fields) => fields.drugStrength.value,
    options: [],
    selectSize: 200,
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 3,
      md: 2,
      lg: 3,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 6,
      md: 6,
      lg: 8,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 4,
    },
    fieldInputStyle: {
      paddingTop: "0.5rem",
    },
  },

  drugNotFound: {
    label: "The drug selected is no longer available.",
    visibility: (fields) =>
      fields.drugName.value &&
      fields.drugFormData.value &&
      fields.drugFormData.value.length === 0,
    type: "header",
    suffix: "",
    fieldLabelStyle: {
      paddingTop: "0",
      fontSize: "2rem",
      whiteSpace: "break-spaces",
    },
    fieldLabelTextStyle: {
      color: colors.magellanRed,
    },
    fieldLabelBreakPoints: {
      xs: 11,
      sm: 11,
      md: 11,
      lg: 11,
    },
    fieldInputBreakpoints: {
      xs: 1,
      sm: 1,
      md: 1,
      lg: 1,
    },
  },

  // step 4
  memberInformation: {
    label: "Member Information",
    type: "header",
    fieldLabelTextStyle: {
      fontWeight: "bold"
    },
    fieldLabelStyle: {
      paddingTop: "0.7rem",
      whiteSpace: "break-spaces",
    },
    fieldLabelBreakPoints: {
      xs: 11,
      sm: 11,
      md: 11,
      lg: 11,
    },
    fieldInputBreakpoints: {
      xs: 1,
      sm: 1,
      md: 1,
      lg: 1,
    },
  },
  patientName: {
    label: "Member Name",
    type: "prefilled",
    render: (fields) =>
      `${fields.beneficiaryFirstName.value} ${fields.beneficiaryLastName.value}`,
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 4,
      md: 6,
      lg: 4,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 6,
      md: 6,
      lg: 8,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 5,
      lg: 4,
    },
    fieldLabelStyle: {
      paddingTop: "0",
    },
  },
  patientHeight: {
    label: "Member Height",
    type: "number",
    units: "inches",
    validations: ["integerStart"],
    inputProps: { maxlength: 5, style: { width: 125 } },
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 4,
      md: 5,
      lg: 4,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 6,
      md: 6,
      lg: 8,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 6,
      lg: 4,
    },
  },
  patientWeight: {
    label: "Member Weight",
    type: "number",
    units: "lbs",
    validations: ["integerStart"],
    inputProps: { maxlength: 5, style: { width: 125 } },
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 4,
      md: 6,
      lg: 4,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 6,
      md: 6,
      lg: 8,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 5,
      lg: 4,
    },
  },
  beneficiaryLocation: {
    label: "Member Residence",
    type: "select",
    inputProps: { style: { width: '30ch' }, },
    options: [
      { value: "12", label: "Home" },
      { value: "25", label: "Skilled Nursing Facility" },
      { value: "53", label: "Nursing Facility (ICF or LTC)" },
      { value: "56", label: "Other Location, not specified" },
    ],
    validations: ["required"],
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 4,
      md: 5,
      lg: 4,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 6,
      md: 6,
      lg: 8,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 6,
      lg: 4,
    },
    fieldStyle: {
      paddingTop: "0",
      paddingBottom: "2rem",
    },
    fieldInputStyle: {
      paddingTop: "0.5rem",
    },
  },
  allergyRadio: {
    label: "Does the member have any allergies",
    type: "allergyRadio",
    defaultValue: "no",
    suffix: "?",
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 4,
      md: 6,
      lg: 4
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 4,
      md: 6,
      lg: 4
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 12
    },
    fieldLabelStyle: {
      paddingTop: "0.5rem",
    },
  },
  allergyInformation: {
    label: "Provide allergen and reaction",
    type: "textarea",
    visibility: (fields) => fields.allergyRadio.value === "yes",
    render: (fields) => fields.allergyRadio.value === "yes",
    validations: ["textFieldRequired"],
    suffix: "",
    fieldLabelStyle: {
      whiteSpace: "break-spaces",
    },
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 12,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 12,
    },
  },
  drugAndDiagnosisInformation: {
    label: "Drug and Diagnosis Information",
    type: "header",
    fieldLabelTextStyle: {
      fontWeight: "bold"
    },
    fieldLabelStyle: {
      paddingTop: "0.7rem",
      whiteSpace: "break-spaces",
    },
    fieldLabelBreakPoints: {
      xs: 11,
      sm: 11,
      md: 11,
      lg: 11,
    },
    fieldInputBreakpoints: {
      xs: 1,
      sm: 1,
      md: 1,
      lg: 1,
    },
  },
  drugNamePrefilled: {
    label: "Drug Name",
    type: "prefilled",
    visibility: (fields) => fields.isCompound.value === "no",
    render: (fields) =>
      fields.isCompound.value === "no" && fields.drugName.value,
    fieldLabelStyle: {
      paddingTop: "0",
    },
    inputProps: { minLength: 3 },
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 4,
      md: 5,
      lg: 4,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 6,
      md: 6,
      lg: 8,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 6,
      lg: 4,
    },
  },
  ndcPrefilled: {
    label: "NDC",
    type: "prefilled",
    visibility: (fields) => fields.isCompound.value === "no" && fields.searchBy.value === "ndc",
    render: (fields) =>
      fields.isCompound.value === "no" && fields.ndc.value,
    fieldLabelStyle: {
      paddingTop: "0",
    },
    inputProps: { minLength: 3 },
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 4,
      md: 5,
      lg: 4,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 6,
      md: 6,
      lg: 8,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 6,
      lg: 4,
    },
  },
  quantityPrefilled: {
    label: "Quantity",
    type: "prefilled",
    visibility: (fields) => fields.isCompound.value === "no",
    render: (fields) =>
      fields.isCompound.value === "no" && fields.quantity.value,
    fieldLabelStyle: {
      paddingTop: "0",
    },
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 4,
      md: 5,
      lg: 4,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 6,
      md: 6,
      lg: 8,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 6,
      lg: 4,
    },
  },
  daysSupplyPrefilled: {
    label: "Days Supply",
    type: "prefilled",
    visibility: (fields) => fields.isCompound.value === "no",
    render: (fields) =>
      fields.isCompound.value === "no" && fields.daysSupply.value,
    fieldLabelStyle: {
      paddingTop: "0",
    },
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 4,
      md: 5,
      lg: 4,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 6,
      md: 6,
      lg: 8,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 6,
      lg: 4,
    },
  },
    doseInput: {
    label: `Dose`,
    validations: ["required"],
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 4,
      md: 5,
      lg: 4,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 6,
      md: 6,
      lg: 8,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 6,
      lg: 4,
    },
  },
  routeInput: {
    label: `Route of Administration`,
    validations: ["required"],
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 4,
      md: 5,
      lg: 4,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 6,
      md: 6,
      lg: 8,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 6,
      lg: 4,
    },
  },
  frequencyInput: {
    label: `Frequency of Administration`,
    validations: ["required"],
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 4,
      md: 5,
      lg: 5,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 6,
      md: 6,
      lg: 7,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 6,
      lg: 4,
    },
  },
  numberRefills: {
    label: "Number of Refills",
    type: "number",
    inputProps: { maxlength: 2 },
    validations: ["positiveIntegerZero"],
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 4,
      md: 5,
      lg: 4,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 6,
      md: 5,
      lg: 8,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 6,
      lg: 4,
    },
  },
  dawSelectionCode: {
    label: "DAW/Product Selection Code",
    type: "select",
    inputProps: { style: { width: '35ch' }, },
    validations: ["required"],
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 4,
      md: 5,
      lg: 4,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 6,
      md: 5,
      lg: 8,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 6,
      lg: 4,
    },
    fieldInputStyle: {
      paddingTop: "0.5rem",
    },
  },

  requestType: {
    label: "Request Type",
    type: "select",
    defaultValue: "New Request",
    inputProps: { style: { width: '24ch' }, },
    validations: ["required"],
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 4,
      md: 6,
      lg: 4,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 6,
      md: 6,
      lg: 8,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 5,
      lg: 4,
    },
    fieldInputStyle: {
      paddingTop: "0.5rem",
    },
    fieldStyle: {
      paddingBottom: "1rem",
    },
  },
  requestBeginDate: {
    label: "PA Request Begin Date",
    type: "date",
    validations: ["required", "dateFmt", "dateLessThan_requestEndDate"],
    afteronChange: ({ fields, setFields }) => {
      setFields({ requestEndDate: fields.requestEndDate.value });
    },
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 4,
      md: 5,
      lg: 4,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 6,
      md: 6,
      lg: 6,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 6,
      lg: 4,
    },
    fieldStyle: {
      paddingBottom: "1rem",
    },
  },
  requestEndDate: {
    label: "PA Request End Date",
    type: "date",
    validations: ["required", "dateFmt", "dateGreaterThan_requestBeginDate"],
    afteronChange: ({ fields, setFields }) => {
      setFields({ requestBeginDate: fields.requestBeginDate.value });
    },
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 4,
      md: 5,
      lg: 4,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 6,
      md: 6,
      lg: 8,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 6,
      lg: 4,
    },
  },
  therapyRadio: {
    label: "Is this a Continuation of Therapy (member is currently using this product)",
    suffix: "?",
    type: "boolean",
    defaultValue: 'no',
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 4,
      md: 6,
      lg: 4
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 4,
      md: 6,
      lg: 4
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 12
    },
    fieldLabelStyle: {
      paddingTop: "0.5rem",
    },
  },
  therapyDate: {
    label: "Date Therapy Initiated",
    type: "date",
    validations: ["required", "dateFmt"],
    visibility: (fields) => fields.therapyRadio.value === "yes",
    render: (fields) => fields.therapyRadio.value === "yes",
    afteronChange: ({ fields, setFields }) => {
      setFields({ therapyDate: fields.therapyDate.value });
    },
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 4,
      md: 5,
      lg: 4,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 6,
      md: 6,
      lg: 6,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 6,
      lg: 4,
    },
  },
  therapyMedicationSelect: {
    label: "How did the Member receive the medication",
    suffix: "?",
    type: "select",
    visibility: (fields) => fields.therapyRadio.value === "yes",
    render: (fields) => fields.therapyRadio.value === "yes",
    options: [
      { value: "01", label: "Received samples" },
      { value: "02", label: "Used different insurance coverage" },
      { value: "03", label: "Started therapy in hospital" },
      { value: "04", label: "Paid out of pocket" },
      { value: "05", label: "Other method" },
    ],
    inputProps: { style: { width: '35ch' }, },
    defaultValue: "New Request",
    validations: ["required"],
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 4,
      md: 6,
      lg: 8
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 4,
      md: 6,
      lg: 4
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 6
    },
    fieldInputStyle: {
      paddingTop: "0.5rem",
    },
    fieldStyle: {
      paddingBottom: "1rem",
    },
  },
  therapyOtherInfo: {
    label: "Other method detail",
    validations: ["required"],
    visibility: (fields) => fields.therapyRadio.value === "yes" && fields.therapyMedicationSelect.value === "05",
    render: (fields) => fields.therapyRadio.value === "yes" && fields.therapyMedicationSelect.value === "05",
    type: "text",
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 4,
      md: 6,
      lg: 8
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 4,
      md: 6,
      lg: 4
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 6
    },
    fieldInputStyle: {
      paddingTop: "0.5rem",
    },
    fieldStyle: {
      paddingBottom: "1rem",
    },
  },
  therapyClinicalOutcome: {
    label: "Describe clinical outcome while on therapy",
    type: "textarea",
    visibility: (fields) => fields.therapyRadio.value === "yes",
    render: (fields) => fields.therapyRadio.value === "yes",
    validations: ["textFieldRequired"],
    suffix: ".",
    fieldLabelStyle: {
      whiteSpace: "break-spaces",
    },
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 12,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 12,
    },
  },
  trialFailure: {
    label: "Tried/Considered Drugs",
    descriptionHTML: {
      __html:
        `<p>Indicate what <a href="/cms/medicalrx/static-assets/documents/provider/forms-and-information/cdl/Medi-Cal_Rx_Contract_Drugs_List_FINAL.pdf" target="_blank" style="font-weight: bold">Contract Drug List</a> (CDL) medications have been tried and/or considered for the specified indications. Include dates of trial, detailed reasons for therapeutic failure.</p>`
    },
    type: "textarea",
    validations: ["textFieldRequired"],
    fieldLabelTextStyle: {
      fontWeight: "bold"
    },
    fieldLabelStyle: {
      whiteSpace: "break-spaces",
    },
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 12,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 12,
    },
  },
  nonCdlMedications: {
    label: "List all other medication(s) not on the CDL that have been tried for the treatment of the member's diagnosis. Include drug name(s), date(s) of therapy, and therapeutic outcome(s). Provide comprehensive and detailed clinical information that is relevant to this request",
    type: "textarea",
    suffix: ".",
    fieldLabelStyle: {
      whiteSpace: "break-spaces",
    },
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 12,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 12,
    },
  },
  offLabelRadio: {
    label: "Is this Request for an off-label use",
    suffix: "?",
    type: "boolean",
    fieldLabelTextStyle: {
      fontWeight: "bold"
    },
    defaultValue: 'no',
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 4,
      md: 6,
      lg: 4
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 4,
      md: 6,
      lg: 4
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 12
    },
    fieldLabelStyle: {
      paddingTop: "0.5rem",
    },
  },
  offLabelDetails: {
    label: "If you are going to use this medication for off-label use or if it exceeds FDA - approved dosing, submit article(s) from major peer-reviewed medical journals and add the supporting documents, in the ATTACHMENTS step",
    type: "textarea",
    visibility: (fields) => fields.offLabelRadio.value === "yes",
    render: (fields) => fields.offLabelRadio.value === "yes",
    validations: ["textFieldRequired"],
    suffix: ".",
    fieldLabelStyle: {
      whiteSpace: "break-spaces",
    },
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 12,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 12,
    },
  },
  alternateShortageRadio: {
    label: "Is this product being requested because an alternate product is on shortage",
    suffix: "?",
    type: "boolean",
    fieldLabelTextStyle: {
      fontWeight: "bold"
    },
    defaultValue: 'no',
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 4,
      md: 6,
      lg: 4
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 4,
      md: 6,
      lg: 4
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 12
    },
    fieldLabelStyle: {
      paddingTop: "0.5rem",
    },
  },
  alternateUnavailableRadio: {
    label: "Is the alternative product unavailable in the local marketplace or region where the member resides",
    suffix: "?",
    type: "boolean",
    visibility: (fields) => fields.alternateShortageRadio.value === "yes",
    render: (fields) => fields.alternateShortageRadio.value === "yes",
    validations: ["required"],
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 4,
      md: 6,
      lg: 4
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 4,
      md: 6,
      lg: 4
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 12
    },
    fieldLabelStyle: {
      paddingTop: "0.5rem",
    },
  },
  alternateShortageConfirmationSelect: {
    label: "The shortage has been confirmed by one of the following",
    type: "select",
    visibility: (fields) => fields.alternateShortageRadio.value === "yes",
    render: (fields) => fields.alternateShortageRadio.value === "yes",
    options: [
      { value: "01", label: "ASHP Drug Shortage" },
      { value: "02", label: "FDA Drug Shortage" },
      { value: "03", label: "Invoice or Wholesaler Notification (include documentation in ATTACHMENTS step)" },
    ],
    inputProps: { style: { width: '35ch' }, },
    validations: ["required"],
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 4,
      md: 6,
      lg: 8
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 4,
      md: 6,
      lg: 4
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 6
    },
    fieldInputStyle: {
      paddingTop: "0.5rem",
    },
    fieldStyle: {
      paddingBottom: "1rem",
    },
  },
  alternateShortageDetails: {
    label: "If unable to confirm shortage with reason above, provide explanation",
    type: "textarea",
    visibility: (fields) => fields.alternateShortageRadio.value === "yes",
    render: (fields) => fields.alternateShortageRadio.value === "yes",
    suffix: ".",
    fieldLabelStyle: {
      whiteSpace: "break-spaces",
    },
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 12,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 12,
    },
  },
  enteralNutritionProductRadio: {
    label: "Is the request for an enteral nutrition product",
    type: "boolean",
    suffix: "?",
    fieldLabelTextStyle: {
      fontWeight: "bold"
    },
    defaultValue: 'no',
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 4,
      md: 6,
      lg: 4
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 4,
      md: 6,
      lg: 4
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 12
    },
    fieldLabelStyle: {
      paddingTop: "0.5rem",
    },
  },
  enteralNdcInput: {
    label: "NDC",
    visibility: (fields) => fields.enteralNutritionProductRadio.value === "yes",
    render: (fields) => fields.enteralNutritionProductRadio.value === "yes",
    sanitize: extractNpi,
    inputProps: { maxlength: 11 },
    validations: ["required", "ndc"],
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 4,
      md: 5,
      lg: 4,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 6,
      md: 6,
      lg: 8,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 6,
      lg: 4,
    },
  },
  enteralKcalInput: {
    label: `Daily Kcal of the Requested Product`,
    visibility: (fields) => fields.enteralNutritionProductRadio.value === "yes",
    render: (fields) => fields.enteralNutritionProductRadio.value === "yes",
    validations: ["required"],
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 4,
      md: 5,
      lg: 6,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 6,
      md: 6,
      lg: 6,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 6,
      lg: 4,
    },
  },
  enteralQuantityInput: {
    label: `Quantity Requested`,
    visibility: (fields) => fields.enteralNutritionProductRadio.value === "yes",
    render: (fields) => fields.enteralNutritionProductRadio.value === "yes",
    validations: ["required"],
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 4,
      md: 5,
      lg: 4,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 6,
      md: 6,
      lg: 8,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 6,
      lg: 4,
    },
  },
  enteralClinicalRationalDetails: {
    label: "If the requested quantity is higher than the established threshold and calorie limit is not met, enter the clinical rationale for why member is unable to meet their nutritional needs with dietary adjustment of regular or altered consistency (soft or puréed) foods",
    type: "textarea",
    visibility: (fields) => fields.enteralNutritionProductRadio.value === "yes",
    render: (fields) => fields.enteralNutritionProductRadio.value === "yes",
    validations: ["textFieldRequired"],
    suffix: ".",
    fieldLabelStyle: {
      whiteSpace: "break-spaces",
    },
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 12,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 12,
    },
  },
  enteralDaysSupplyInput: {
    label: `Days' Supply Requested`,
    visibility: (fields) => fields.enteralNutritionProductRadio.value === "yes",
    render: (fields) => fields.enteralNutritionProductRadio.value === "yes",
    validations: ["required"],
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 4,
      md: 5,
      lg: 4,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 6,
      md: 6,
      lg: 8,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 6,
      lg: 4,
    },
  },
  enteralAdministrationSelect: {
    label: "Enteral Administration",
    type: "select",
    visibility: (fields) => fields.enteralNutritionProductRadio.value === "yes",
    render: (fields) => fields.enteralNutritionProductRadio.value === "yes",
    options: [
      { value: "01", label: "Oral" },
      { value: "02", label: "Tube Fed" },
    ],
    inputProps: { style: { width: '20ch' }, },
    validations: ["required"],
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 4,
      md: 5,
      lg: 6,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 6,
      md: 6,
      lg: 6,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 6,
      lg: 4,
    },
    fieldInputStyle: {
      paddingTop: "0.5rem",
    },
    fieldStyle: {
      paddingBottom: "1rem",
    },
  },
  enteralRationalDetails: {
    label: "List reasons for requesting an enteral nutrition product. For specialized category of enteral nutrition product, provide an additional ICD-10 code(s) as needed",
    type: "textarea",
    visibility: (fields) => fields.enteralNutritionProductRadio.value === "yes",
    render: (fields) => fields.enteralNutritionProductRadio.value === "yes",
    validations: ["textFieldRequired"],
    suffix: ".",
    fieldLabelStyle: {
      whiteSpace: "break-spaces",
    },
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 12,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 12,
    },
  },
  enteralInfantProductRadio: {
    label: "Is this request for an infant product",
    type: "boolean",
    defaultValue: 'no',
    suffix: "?",
    visibility: (fields) => fields.enteralNutritionProductRadio.value === "yes",
    render: (fields) => fields.enteralNutritionProductRadio.value === "yes",
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 4,
      md: 6,
      lg: 4
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 4,
      md: 6,
      lg: 4
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 12
    },
    fieldLabelStyle: {
      paddingTop: "0.5rem",
    },
  },
  enteralInfantBirthWeightInput: {
    label: "Birth Weight",
    type: "number",
    units: "lbs",
    validations: ["required", "positiveNumber"],
    visibility: (fields) => fields.enteralNutritionProductRadio.value === "yes" && fields.enteralInfantProductRadio.value === "yes",
    render: (fields) => fields.enteralNutritionProductRadio.value === "yes" && fields.enteralInfantProductRadio.value === "yes",
    inputProps: { maxlength: 3, style: { width: 125 } },
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 4,
      md: 6,
      lg: 4,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 6,
      md: 6,
      lg: 8,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 5,
      lg: 4,
    },
  },
  enteralInfantCurrentWeightInput: {
    label: "Current Weight",
    type: "number",
    units: "lbs",
    validations: ["required", "positiveNumber"],
    visibility: (fields) => fields.enteralNutritionProductRadio.value === "yes" && fields.enteralInfantProductRadio.value === "yes",
    render: (fields) => fields.enteralNutritionProductRadio.value === "yes" && fields.enteralInfantProductRadio.value === "yes",
    inputProps: { maxlength: 3, style: { width: 125 } },
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 4,
      md: 6,
      lg: 4,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 6,
      md: 6,
      lg: 8,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 5,
      lg: 4,
    },
  },
  enteralInfantLbwInput: {
    label: "For infant low birth weight (LBW) products, indicate if the member was born prior to 37 weeks of gestation. Specify # of weeks",
    type: "number",
    units: "weeks",
    suffix: ".",
    validations: ["positiveNumber"],
    visibility: (fields) => fields.enteralNutritionProductRadio.value === "yes" && fields.enteralInfantProductRadio.value === "yes",
    render: (fields) => fields.enteralNutritionProductRadio.value === "yes" && fields.enteralInfantProductRadio.value === "yes",
    inputProps: { maxlength: 2, style: { width: 125 } },
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 4,
      md: 6,
      lg: 8,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 6,
      md: 6,
      lg: 4,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 5,
      lg: 9,
    },
  },
  topicalProductRadio: {
    label: "Is this a topical product",
    type: "boolean",
    suffix: "?",
    fieldLabelTextStyle: {
      fontWeight: "bold"
    },
    defaultValue: 'no',
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 4,
      md: 6,
      lg: 4
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 4,
      md: 6,
      lg: 4
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 12
    },
    fieldLabelStyle: {
      paddingTop: "0.5rem",
    },
  },
  topicalApplicationDetails: {
    label: "Area(s) of Application",
    type: "textarea",
    visibility: (fields) => fields.topicalProductRadio.value === "yes",
    render: (fields) => fields.topicalProductRadio.value === "yes",
    validations: ["textFieldRequired"],
    suffix: ".",
    fieldLabelStyle: {
      whiteSpace: "break-spaces",
    },
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 12,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 12,
    },
  },
  topicalAreaInput: {
    label: `Size of the Affected Area(s)`,
    visibility: (fields) => fields.topicalProductRadio.value === "yes",
    render: (fields) => fields.topicalProductRadio.value === "yes",
    validations: ["required"],
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 4,
      md: 5,
      lg: 6,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 6,
      md: 6,
      lg: 6,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 6,
      lg: 4,
    },
  },
  topicalWoundCareRadio: {
    label: "Is the product being used for wound care",
    type: "boolean",
    defaultValue: 'no',
    suffix: "?",
    visibility: (fields) => fields.topicalProductRadio.value === "yes",
    render: (fields) => fields.topicalProductRadio.value === "yes",
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 4,
      md: 6,
      lg: 4
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 4,
      md: 6,
      lg: 4
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 12
    },
    fieldLabelStyle: {
      paddingTop: "0.5rem",
    },
  },
  topicalNecroticRadio: {
    label: "Is there presence of necrotic tissue",
    type: "boolean",
    defaultValue: 'no',
    suffix: "?",
    visibility: (fields) => fields.topicalProductRadio.value === "yes" && fields.topicalWoundCareRadio.value === "yes",
    render: (fields) => fields.topicalProductRadio.value === "yes" && fields.topicalWoundCareRadio.value === "yes",
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 4,
      md: 6,
      lg: 4
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 4,
      md: 6,
      lg: 4
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 12
    },
    fieldLabelStyle: {
      paddingTop: "0.5rem",
    },
  },
  additionalInformation: {
    label: "Additional Clinical Information",
	  descriptionHTML: {
      __html:
        `<p>Provide clinical information, lab values, or other relevant information to this case. List comorbidities or additional diagnosis.</p>`
    },
    type: "additionalInfo",
    fieldLabelTextStyle: {
      fontWeight: "bold"
    },
    validations: ["textFieldRequired"],
    suffix: "",
    fieldLabelStyle: {
      whiteSpace: "break-spaces",
    },
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 12,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 12,
    },
  },

  //step 5

  uploadHeader1: {
    label:
      "If you are unable to upload attachments electronically, please fax your attachments to 1-800-869-4325",
    descriptionHTML: {
      __html:
        `<p>Please include the members first and last name, date of birth, and Medi-Cal Rx identification number on a cover page.</p>
    <p>Please attach all chart/lab note(s), invoice(s), compendia literature(s), and any other documentation deemed pertinent to establish medical necessity.</p>
    <p>This information will be used to ensure a thorough clinical review of the PA request.</p>`
    },
    type: "header",
    suffix: "",
    fieldLabelStyle: {
      whiteSpace: "unset",
      maxWidth: "100%",
      fontSize: "1.2rem",
      paddingLeft: "20vw",
    },
    fieldLabelBreakPoints: {
      xs: 11,
      sm: 11,
      md: 11,
      lg: 11,
    },
    fieldInputBreakpoints: {
      xs: 1,
      sm: 1,
      md: 1,
      lg: 1,
    },
  },

  uploadHeader2: {
    label:
      "Please include the Member First Name, Last Name, Date of Birth, and Identification Number on a cover page.",
    type: "header",
    suffix: "",
    fieldLabelStyle: {
      whiteSpace: "unset",
      maxWidth: "100%",
      fontSize: "1.2rem",
      paddingLeft: "18vw",
    },
    fieldLabelBreakPoints: {
      xs: 11,
      sm: 11,
      md: 11,
      lg: 11,
    },
    fieldInputBreakpoints: {
      xs: 1,
      sm: 1,
      md: 1,
      lg: 1,
    },
  },

  attachments: {
    label: "",
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 12,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 12,
    },
    type: "upload",
  },

  //step 6

  uploadList: {
    label: "",
    suffix: "",
    fieldInputBreakpoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 12,
    },
  },
  compoundDispUnitFormInd: {
    label: "Compound Dispensing Unit Form Indicator",
    visibility: (field) => field.isCompound.value === "yes",
    type: "select",
    options: [
      { value: "1", label: "Each" },
      { value: "2", label: "Grams" },
      { value: "3", label: "Milliliters" },
    ],
    validations: ["required"],
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 12,
      md: 5,
      lg: 4,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 12,
      md: 7,
      lg: 8,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 10,
    },
    fieldInputStyle: {
      paddingTop: "0.5rem",
    },
  },

  compoundDosageFormCd: {
    visibility: (field) => field.isCompound.value === "yes",
    label: "Compound Dosage Form",
    type: "select",
    options: [
      { value: "01", label: "CAPSULE" }, // CAPSULE
      { value: "03", label: "CREAM" }, // CREAM
      { value: "15", label: "ELIXIR" }, // ELIXIR
      { value: "06", label: "EMULSION" }, // EMULSION
      { value: "18", label: "ENEMA" }, // ENEMA
      { value: "07", label: "LIQUID" }, // LIQUID
      { value: "13", label: "LOTION" }, // LOTION
      { value: "17", label: "LOZENGE" }, // LOZENGE
      { value: "02", label: "OINTMENT" }, // OINTMENT
      { value: "05", label: "POWDER" }, // POWDER
      { value: "14", label: "SHAMPOO" }, // SHAMPOO
      { value: "11", label: "SOLUTION" }, // SOLUTION
      { value: "04", label: "SUPPOSITORY" }, // SUPPOSITORY
      { value: "12", label: "SUSPENSION" }, // SUSPENSION
      { value: "16", label: "SYRUP" }, // SYRUP
      { value: "10", label: "TABLET" }, // TABLET
    ],
    validations: ["required"],
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 12,
      md: 5,
      lg: 4,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 12,
      md: 7,
      lg: 8,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 10,
    },
    fieldInputStyle: {
      paddingTop: "0.5rem",
    },
  },
  diagnosisCodes: {},
  diagnosisCode0: {
    label: `ICD-10 Diagnosis Code`,
    validations: ["required"],
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 4,
      md: 5,
      lg: 4,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 6,
      md: 6,
      lg: 8,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 6,
      lg: 4,
    },
  },
  diagnosisCode1: {
    label: `ICD-10 Diagnosis Code`,
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 4,
      md: 5,
      lg: 4,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 6,
      md: 6,
      lg: 8,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 6,
      lg: 4,
    },
  },
  DRUG_INFO_HEADER: {
    visibility: (field) => field.isCompound.value === "yes",
    label:
      "Please have the pharmacy submit the compound drug claim prior to PA submission",
    type: "header",
    fieldLabelTextStyle: {
      fontWeight: "bold",
      fontSize: "1.25rem",
    },
    suffix: "",
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 12,
    },
    fieldInputBreakpoints: {
      xs: 1,
      sm: 1,
      md: 1,
      lg: 1,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 12,
    },
  },
  compoundIngredientCount: {},
  compounds: {
    type: "compoundTable",
    visibility: (fields) => fields.isCompound.value === "yes",
    onChange: (value, { setFields }) => {
      setFields({ ndc: value[0].productId, quantity: 0, daysSupply: 0 });
    },
    validations: ["compounds"],
    fieldLabelBreakPoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 12,
    },
    fieldInputBreakpoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 12,
    },
    fieldBreakpoints: {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 12,
    },
  }
};

export default PaFormFields;
